import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { useAuth } from "@/context/AuthContext";
import { apiError } from "@/lib/api";
import { HardHat, Loader2, Sparkles } from "lucide-react";

export default function Login() {
  const { login, startDemo } = useAuth();
  const nav = useNavigate();
  const [params] = useSearchParams();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [demoLoading, setDemoLoading] = useState(false);

  // Show a friendly message when the axios interceptor bounces us here.
  useEffect(() => {
    if (params.get("reason") === "session_expired") {
      setError("Your session expired. Please sign in again.");
    }
  }, [params]);

  async function onSubmit(e) {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await login(email, password);
      const next = params.get("next") || "/projects";
      nav(next, { replace: true });
    } catch (err) {
      setError(apiError(err));
    } finally {
      setLoading(false);
    }
  }

  async function onDemo() {
    setError("");
    setDemoLoading(true);
    try {
      await startDemo();
      nav("/projects", { replace: true });
    } catch (err) {
      setError(apiError(err));
    } finally {
      setDemoLoading(false);
    }
  }

  return (
    <div className="min-h-screen grid md:grid-cols-2">
      <div className="hidden md:flex blueprint-grid text-white flex-col justify-between p-12">
        <div className="flex items-center gap-3">
          <div className="h-11 w-11 rounded-md bg-brandblue flex items-center justify-center shadow-lg">
            <HardHat className="h-6 w-6" strokeWidth={2.5} />
          </div>
          <div>
            <div className="font-display font-bold text-2xl">BuildTrack</div>
            <div className="text-xs uppercase tracking-widest text-slate-300">Built for builders</div>
          </div>
        </div>
        <div className="space-y-3 max-w-md">
          <h2 className="font-display text-4xl font-bold leading-tight">
            Every dollar. <br />Every trade. <br />
            <span className="text-brandblue">In control.</span>
          </h2>
          <p className="text-slate-300 text-base leading-relaxed">
            Track quotes, variations and partial payments across every trade on your build.
          </p>
        </div>
        <div className="text-xs text-slate-400 uppercase tracking-widest">AUD · Mobile‑first · Site‑ready</div>
      </div>

      <div className="flex items-center justify-center px-4 py-8 sm:p-6 md:p-12 bg-background">
        <Card className="w-full max-w-md p-6 sm:p-8 border border-border shadow-sm">
          <div className="flex md:hidden items-center gap-2 mb-6">
            <div className="h-9 w-9 rounded-md bg-navy flex items-center justify-center">
              <HardHat className="h-5 w-5 text-white" strokeWidth={2.5} />
            </div>
            <div className="font-display font-bold text-xl text-slate-900">BuildTrack</div>
          </div>
          <div className="mb-6 sm:mb-8">
            <div className="text-xs font-semibold uppercase tracking-widest text-slate-500 mb-2">Sign in</div>
            <h1 className="font-display text-2xl sm:text-3xl font-bold text-slate-900">Welcome back</h1>
            <p className="text-sm text-slate-500 mt-1">Access your projects and trades.</p>
          </div>

          <form onSubmit={onSubmit} className="space-y-5" data-testid="login-form">
            <div className="space-y-1.5">
              <Label htmlFor="email" className="text-xs font-semibold uppercase tracking-wider text-slate-600">Email</Label>
              <Input id="email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} data-testid="login-email" className="h-12" placeholder="you@builder.com.au" />
            </div>
            <div className="space-y-1.5">
              <div className="flex items-center justify-between">
                <Label htmlFor="password" className="text-xs font-semibold uppercase tracking-wider text-slate-600">Password</Label>
                <Link to="/forgot-password" className="text-xs text-brandblue hover:underline font-semibold" data-testid="link-forgot">Forgot password?</Link>
              </div>
              <Input id="password" type="password" required value={password} onChange={(e) => setPassword(e.target.value)} data-testid="login-password" className="h-12" placeholder="••••••••" />
            </div>
            {error && (
              <div className="text-sm text-status-danger-text bg-status-danger-bg border border-status-danger-border rounded-md px-3 py-2" data-testid="login-error">
                {error}
              </div>
            )}
            <Button type="submit" disabled={loading} data-testid="login-submit" className="w-full h-12 bg-brandblue hover:bg-brandblue-hover text-white text-base font-semibold">
              {loading && <Loader2 className="h-4 w-4 animate-spin" />}
              {loading ? "Signing in…" : "Sign in"}
            </Button>
          </form>

          <div className="relative my-6">
            <div className="absolute inset-0 flex items-center"><div className="w-full h-px bg-border" /></div>
            <div className="relative flex justify-center text-[11px] uppercase tracking-widest">
              <span className="bg-white px-3 text-slate-400">or explore instantly</span>
            </div>
          </div>

          <Button
            type="button"
            variant="outline"
            onClick={onDemo}
            disabled={demoLoading}
            data-testid="btn-try-demo"
            className="w-full h-12 border-2 border-navy bg-white text-navy hover:bg-navy hover:text-white text-base font-semibold transition-colors"
          >
            {demoLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />}
            {demoLoading ? "Loading demo…" : "Try Demo — no signup"}
          </Button>
          <p className="text-[11px] text-slate-400 text-center mt-2">
            Pre-loaded with a real build project, 7 trades and mixed payments.
          </p>

          <div className="text-sm text-slate-500 mt-6 text-center">
            New to BuildTrack?{" "}
            <Link to="/register" className="text-brandblue font-semibold hover:underline" data-testid="link-register">
              Create an account
            </Link>
          </div>
          <p className="text-[11px] text-slate-400 text-center mt-4">
            <Link to="/terms" className="hover:text-brandblue hover:underline">Terms</Link>
            <span className="mx-1.5">·</span>
            <Link to="/privacy" className="hover:text-brandblue hover:underline">Privacy</Link>
          </p>
        </Card>
      </div>
    </div>
  );
}
